import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Headphones, ChevronDown } from "lucide-react";

/**
 * Header — global navbar, palette navy/white/mint stricte.
 * - bg #ffffff permanent (border-bottom #E8EDF5)
 * - Liens #64748B / actif #0F2B5B 500 avec underline 2px #4FC3F7
 * - CTAs : Support à distance #F1F5F9 + Démo #0F2B5B
 */

const DMP_SUBNAV = [
  { to: "/services/dmp", label: "MetaBridge DMP", desc: "Connecteur DMP pour votre logiciel métier" },
  { to: "/services/dmp/certification-cnda", label: "Certification CNDA", desc: "Parcours d'homologation pour éditeurs LPS" },
];

const PLATEFORME_SUBNAV = [
  { to: "/plateforme", label: "Vue d'ensemble", desc: "Votre plateforme de télésanté en marque blanche" },
  { to: "/plateforme/quota-telesoin", label: "Quota télésoin & contrats", desc: "Plafond 20 %, 4 actes/an/patient, contrats Ordre" },
  { to: "/plateforme/facturation-electronique", label: "Facturation Factur-X", desc: "TVA santé, e-reporting, mandat comptable (2026)" },
  { to: "/plateforme/messagerie-instantanee", label: "Messagerie instantanée", desc: "Coordination temps réel entre professionnels" },
  { to: "/plateforme/reseau-proximite", label: "Réseau de proximité", desc: "Cartographie Annuaire Santé (FHIR ANS)" },
  { to: "/studio", label: "Studio — Configurer", desc: "Générez votre sandbox personnalisée en 10 min" },
];

const NET_SUBNAV = [
  { to: "/net", label: "Découvrir NET", desc: "Le collaborateur numérique souverain — Mistral, HDS" },
  { to: "/net/opticien", label: "NET Opticiens", desc: "Réponses instantanées et fiches verriers en langage naturel" },
  { to: "/net/audioprothesiste", label: "NET Audioprothésistes", desc: "Comptes rendus préparés, documentation fournisseurs" },
  { to: "/net/groupements", label: "NET Groupements", desc: "IA mutualisée, procédures et offres homogènes" },
  { to: "/net/industriels", label: "NET Industriels", desc: "Vos fiches techniques interrogeables par les pros" },
  { to: "/net/editeurs", label: "NET Éditeurs", desc: "API expliquées, intégrations guidées" },
];

const NAV = [
  { to: "/plateforme", label: "Plateforme", dropdown: PLATEFORME_SUBNAV },
  { to: "/net", label: "NET", dropdown: NET_SUBNAV },
  { to: "/solutions", label: "Solutions" },
  { to: "/services/dmp", label: "DMP", dropdown: DMP_SUBNAV },
  { to: "/mssante", label: "MSSanté" },
  { to: "/services/ins", label: "INS" },
  { to: "/services/psc", label: "PSC" },
  { to: "/services/fhir", label: "FHIR" },
  { to: "/conformite", label: "Conformité" },
  { to: "/blog", label: "Blog" },
];

const linkBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 14,
  color: "#64748B",
  textDecoration: "none",
  paddingBottom: 4,
  borderBottom: "2px solid transparent",
  transition: "color .15s ease, border-color .15s ease",
};
const linkActive = {
  color: "#0F2B5B",
  fontWeight: 500,
  borderBottomColor: "#4FC3F7",
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const dropdownTimer = useRef(null);

  useEffect(() => { setOpen(false); setOpenDropdown(null); }, [location.pathname]);

  const openMenu = (label) => {
    if (dropdownTimer.current) clearTimeout(dropdownTimer.current);
    setOpenDropdown(label);
  };
  const scheduleClose = () => {
    if (dropdownTimer.current) clearTimeout(dropdownTimer.current);
    dropdownTimer.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50"
      style={{
        background: "#ffffff",
        borderBottom: "0.5px solid #E8EDF5",
      }}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-logo">
          <span
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg overflow-hidden"
            style={{ background: "#0F2B5B", boxShadow: "0 4px 12px -6px rgba(15,43,91,0.35)" }}
          >
            <img src="/logos/metaconnect.jpeg" alt="MetaConnect" className="h-full w-full object-cover" />
          </span>
          <div className="leading-none">
            <div className="font-display font-semibold text-[17px]" style={{ color: "#0F2B5B" }}>MetaConnect</div>
            <div className="text-[10px] mt-0.5 tracking-wider uppercase" style={{ color: "#64748B" }}>
              e-santé · interopérable
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-7 text-[14px]" data-testid="nav-desktop">
          {NAV.map((n) => (
            n.dropdown ? (
              <div
                key={n.to}
                className="relative"
                onMouseEnter={() => openMenu(n.label)}
                onMouseLeave={scheduleClose}
              >
                <NavLink
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  style={({ isActive }) => ({ ...linkBase, ...(isActive ? linkActive : {}) })}
                  onClick={() => setOpenDropdown(null)}
                >
                  {n.label}
                  <ChevronDown size={13} className={`transition-transform ${openDropdown === n.label ? "rotate-180" : ""}`} />
                </NavLink>
                {openDropdown === n.label && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3" data-testid={`nav-${n.label.toLowerCase()}-dropdown`}>
                    <div
                      className="min-w-[320px] p-2 rounded-xl"
                      style={{
                        background: "#ffffff",
                        border: "0.5px solid #E8EDF5",
                        boxShadow: "0 20px 48px -16px rgba(15,43,91,0.18)",
                      }}
                    >
                      {n.dropdown.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          className="block px-3 py-2.5 rounded-lg transition-colors hover:bg-[#F0F9FF]"
                          data-testid={`nav-${n.label.toLowerCase()}-${s.to.split("/").pop()}`}
                        >
                          <div className="text-[13.5px] font-medium" style={{ color: "#0F2B5B" }}>{s.label}</div>
                          <div className="text-[11.5px] mt-0.5" style={{ color: "#64748B" }}>{s.desc}</div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                style={({ isActive }) => ({ ...linkBase, ...(isActive ? linkActive : {}) })}
              >
                {n.label}
              </NavLink>
            )
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/support-distant"
            data-testid="nav-support-cta"
            title="Autoriser une prise en main à distance par MetaConnect"
            className="inline-flex items-center gap-1.5 transition-colors"
            style={{
              background: "#F1F5F9",
              color: "#0F2B5B",
              borderRadius: 999,
              padding: "8px 14px",
              fontSize: 12.5,
              fontWeight: 500,
              border: 0,
              textDecoration: "none",
            }}
          >
            <Headphones size={14} />
            Support à distance
          </Link>
          <Link
            to="/demo"
            data-testid="nav-demo-cta"
            className="inline-flex items-center"
            style={{
              background: "#0F2B5B",
              color: "#ffffff",
              borderRadius: 999,
              padding: "9px 18px",
              fontSize: 13,
              fontWeight: 500,
              textDecoration: "none",
              boxShadow: "0 8px 22px -10px rgba(15,43,91,0.45)",
            }}
          >
            Planifier une démo
          </Link>
        </div>

        <button
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2"
          style={{ color: "#0F2B5B" }}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="md:hidden"
          style={{
            background: "#ffffff",
            borderTop: "0.5px solid #E8EDF5",
          }}
        >
          <div className="px-5 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <div key={n.to}>
                <NavLink
                  to={n.to}
                  data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                  style={({ isActive }) => ({
                    display: "block",
                    padding: "8px 0",
                    fontSize: 15,
                    color: isActive ? "#0F2B5B" : "#64748B",
                    fontWeight: isActive ? 500 : 400,
                    textDecoration: "none",
                  })}
                >
                  {n.label}
                </NavLink>
                {n.dropdown && (
                  <div className="ml-3 mt-1 mb-2 pl-3" style={{ borderLeft: "0.5px solid #E8EDF5" }}>
                    {n.dropdown.map((s) => (
                      <Link
                        key={s.to}
                        to={s.to}
                        className="block py-1"
                        style={{ fontSize: 13, color: "#64748B", textDecoration: "none" }}
                        data-testid={`nav-mobile-${n.label.toLowerCase()}-${s.to.split("/").pop()}`}
                      >
                        {s.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/support-distant"
              data-testid="nav-mobile-support-cta"
              className="inline-flex items-center justify-center gap-1.5"
              style={{
                background: "#F1F5F9", color: "#0F2B5B",
                borderRadius: 999, padding: "10px 18px", fontSize: 13, fontWeight: 500,
                textDecoration: "none", marginTop: 8,
              }}
            >
              <Headphones size={14} />
              Support à distance
            </Link>
            <Link
              to="/demo"
              data-testid="nav-mobile-demo-cta"
              className="inline-flex items-center justify-center"
              style={{
                background: "#0F2B5B", color: "#ffffff",
                borderRadius: 999, padding: "10px 18px", fontSize: 13, fontWeight: 500,
                textDecoration: "none",
              }}
            >
              Planifier une démo
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
